/**
 * Node Registry for managing editor nodes
 */
export class NodeRegistry {
  constructor() {
    this.nodes = new Map();
  }

  /**
   * Register a node class
   * @param {string} type - Node type (e.g., 'image', 'tag', 'color')
   * @param {class} NodeClass - Class extending EditorNode
   */
  register(type, NodeClass) {
    this.nodes.set(type, NodeClass);
  }

  /**
   * Get a node class by type
   * @param {string} type
   * @returns {class|undefined}
   */
  get(type) {
    return this.nodes.get(type);
  }

  /**
   * Create a node instance
   * @param {string} type
   * @param {Object} options
   * @returns {EditorNode}
   */
  create(type, options) {
    const NodeClass = this.get(type);
    if (!NodeClass) {
      throw new Error(`Node type "${type}" not registered`);
    }
    return new NodeClass(options);
  }

  /**
   * Render a node element
   * @param {string} type
   * @param {Object} data
   * @param {Object} context
   * @returns {HTMLElement}
   */
  render(type, data, context) {
    const instance = this.create(type, context);
    return instance.render(data, context);
  }

  /**
   * Update a node element
   * @param {string} type
   * @param {HTMLElement} element
   * @param {Object} data
   * @param {Object} context
   */
  update(type, element, data, context) {
    const instance = this.create(type, context);
    if (instance.update) {
      instance.update(element, data, context);
    }
  }

  /**
   * Serialize a node element to string
   * @param {string} type
   * @param {HTMLElement} element
   * @param {Object} context
   * @returns {string}
   */
  serialize(type, element, context) {
    const instance = this.create(type, context);
    if (!instance.serialize) {
      throw new Error(`Node type "${type}" does not support serialize`);
    }
    return instance.serialize(element);
  }
}

// Singleton instance
export const nodeRegistry = new NodeRegistry();
